import { Component, computed, input, model } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { HlmFieldImports } from '@mb/ui/field';
import { HlmInputImports } from '@mb/ui/input';
import type { OrgRoleOption } from '../../services/org-chart-api.service';

/**
 * The roles of a position: one chip per role it has, and a list of the ones
 * it could be given.
 *
 * Its own component because two sheets show it — the position's form and the
 * panel behind its shield, where the customer asked on 2026-10-31 to change
 * the roles while looking at what they grant. One picker, so the two cannot
 * drift apart.
 *
 * It only edits the selection; saving it is the sheet's business. The host is
 * `display: contents`, so the chips and the list sit in the surrounding field
 * as if they were written there.
 */
@Component({
  selector: 'org-role-picker',
  standalone: true,
  imports: [TranslocoPipe, HlmFieldImports, HlmInputImports],
  host: { class: 'contents' },
  template: `
    @if (selectedRoles().length) {
      <div class="flex flex-wrap gap-1">
        @for (role of selectedRoles(); track role.id) {
          <span
            class="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
          >
            {{ role.name }}
            <button
              type="button"
              class="text-muted-foreground hover:text-foreground"
              [title]="'organigram.panel.removeRole' | transloco"
              (click)="remove(role.id)"
            >
              <span class="material-icons text-sm">close</span>
            </button>
          </span>
        }
      </div>
    }
    @if (availableRoles().length) {
      <select
        hlmInput
        [value]="''"
        (change)="add($any($event.target).value); $any($event.target).value = ''"
      >
        <option value="" disabled>
          {{ 'organigram.panel.rolePlaceholder' | transloco }}
        </option>
        @for (role of availableRoles(); track role.id) {
          <option [value]="role.id">{{ role.name }}</option>
        }
      </select>
    } @else if (!selectedRoles().length) {
      <hlm-field-description>{{
        'organigram.panel.noRoles' | transloco
      }}</hlm-field-description>
    }
  `,
})
export class OrgRolePickerComponent {
  /** Every role the position could have. */
  readonly roles = input.required<readonly OrgRoleOption[]>();
  /** The ids it has, in the order they were given. */
  readonly selected = model.required<string[]>();

  readonly selectedRoles = computed(() => {
    const ids = new Set(this.selected());
    return this.roles().filter((role) => ids.has(role.id));
  });

  readonly availableRoles = computed(() => {
    const ids = new Set(this.selected());
    return this.roles().filter((role) => !ids.has(role.id));
  });

  add(roleId: string): void {
    if (!roleId || this.selected().includes(roleId)) return;
    this.selected.update((ids) => [...ids, roleId]);
  }

  remove(roleId: string): void {
    this.selected.update((ids) => ids.filter((id) => id !== roleId));
  }
}
